/* eslint import/no-named-as-default-member: 0 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Translation, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Pagination from 'components/v2/Pagination';
import {
  COMPLETE_STOP,
  PROVIDE_POD,
  ADD_CONTAINER,
  CONFIRM_APPT,
  ENTER_REQUIRED_DATES,
  SCHEDULE_APPT,
  PROVIDE_ARRIVAL_INFORMATION,
  ADD_DOCUMENTS,
} from 'constants/next-actions';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import { getShipperStopDocumentRequirementsFromConfig } from 'helpers/stops';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import MinimumDocumentRequirements from 'components/modules/shipments/MinimumDocumentRequirements';
import CargomaticModal from 'components/v2/modal';
import Alert from 'components/ui/Alert';
import NextAction from './NextAction';
import StopCompleteModal from '../shipments/stops/StopCompleteModal';
import StopEquipmentModal from '../shipments/stops/StopEquipmentModal';
import AddAppointmentModal from '../../ui/tailwind/modals/AddAppointmentModal';
import ShipmentWebAPIUtils, {
  completeStop,
} from '../../../utils/ShipmentWebAPIUtils';
import StopArrivedModal from '../shipments/stops/StopArrivedModal';
import ShipmentsActionCreators from '../../../actions/ShipmentsActionCreators';
import { FEATURE_FLAG_ENABLED_BREADCRUMB } from 'constants/temp-feature-flag-breadcrumb';

const ACTIONS_WITHOUT_EDIT = [CONFIRM_APPT];

export default function NextActionsList({
  actions,
  actionType2,
  isCustomerView,
  perPage,
  page,
  pages,
  total,
  onPageClick,
  setIsModalOpen,
  onShipmentClosed,
  selectedAction,
  setSelectedAction,
  setShipmentUpdatedOnModalClose,
}) {
  const history = useHistory();
  const user = SessionStore.getUser();
  const { t } = useTranslation();
  const setRedirect = (path) => history.push(path);

  const [shipperStopDocumentRequirements, setShipperStopDocumentRequirements] =
    useState({});

  useEffect(() => {
    if (
      selectedAction?.shipment &&
      featureEnabled(CONFIGURED_FEATURES.SSR) &&
      isIntermodalShipmentType(selectedAction?.shipment) &&
      selectedAction?.shipment?.shipper
    ) {
      const getShipperStopDocRequirements = async () => {
        const { shipper } = selectedAction.shipment;
        const shipperId = shipper?._id || shipper;
        const lookup =
          await getShipperStopDocumentRequirementsFromConfig(shipperId);
        setShipperStopDocumentRequirements(lookup);
      };
      getShipperStopDocRequirements();
    }
  }, [selectedAction]);

  function handleNextActionClick(action) {
    setIsModalOpen(true);
    setSelectedAction(action);
    handlePageClickRedirect(action);
  }

  function handleCloseNextActionModal() {
    setIsModalOpen(false);
    setSelectedAction(null);
  }

  function handlePageClick(selectedPage) {
    onPageClick(selectedPage);
  }

  function handlePageClickRedirect({ action, shipment }) {
    if (action === ENTER_REQUIRED_DATES) {
      console.log('USER TYPE: ', user._type);
      const link =
        user._type === 'Admin'
          ? `/shipments/${shipment._id}`
          : `/shipments/${shipment._id}?action=set_dates`;
      console.log('LINK: ', link);
      setRedirect(link);
    }
  }

  async function handleStopComplete(shipment, stop, file, meta) {
    try {
      const response = await completeStop(shipment, stop, file, meta);
      if (
        stop.action === 'deliver' &&
        response.entity.job.status === 'job_closed'
      ) {
        onShipmentClosed(shipment);
      }
      handleCloseNextActionModal();
      setShipmentUpdatedOnModalClose(true);
      showToast(t(COMPLETE_STOP), t('COMPLETE_STOP_SUCCESS'));
    } catch (e) {
      console.error(e);
      showToast(t(COMPLETE_STOP), t('COMPLETE_STOP_FAIL'), 'error');
    }
  }

  function handleStopEquipmentUpdateSuccess() {
    if (selectedAction.action === ADD_CONTAINER) {
      handleCloseNextActionModal();
      setShipmentUpdatedOnModalClose(true);
    }
    showToast(t(COMPLETE_STOP), t('ADD_CONTAINER_SUCCESS'));
  }

  function handleStopEquipmentUpdateFail(e) {
    console.error(e);
    showToast(t(COMPLETE_STOP), t('ADD_CONTAINER_FAIL'), 'error');
  }

  async function handleScheduleAppt(updates) {
    try {
      const response =
        await ShipmentWebAPIUtils.updateShipmentStopSchedulingResponse(
          selectedAction.shipment,
          selectedAction.nextStop,
          updates,
        );
      if (response.entity.success) {
        showToast(t(PROVIDE_POD), t('SCHEDULE_APPT_SUCCESS'));
        handleCloseNextActionModal();
        setShipmentUpdatedOnModalClose(true);
      } else {
        throw new Error(t('ERROR_UPDATING_APPOINTMENT'));
      }
    } catch (e) {
      console.error(e);
      showToast(t('SCHEDULE_APPOINTMENT'), t('SCHEDULE_APPT_FAIL'), 'error');
    }
  }

  function renderWarningForAccessorialDocumentsConitionally(shipment) {
    if (shipment.accessorial?.some((acc) => acc.missing_docs === true && !FEATURE_FLAG_ENABLED_BREADCRUMB)) {
      return (
        <Alert type="warning" className="m-4">
          <Translation>
            {(t, { i18n }) => (
              <>
                {t(
                  'ACCESSORIAL_DOCUMENTS_MUST_BE_UPLOADED_WITHIN_48_HOURS_OF_DELIVERY_OR_WILL_BE_AUTO-DECLINED',
                )}
              </>
            )}
          </Translation>
        </Alert>
      );
    }
    return null;
  }

  function renderActionModal() {
    if (!selectedAction) {
      return null;
    }

    if (
      selectedAction.action === SCHEDULE_APPT ||
      selectedAction.action === CONFIRM_APPT
    ) {
      return (
        <AddAppointmentModal
          stop={selectedAction.nextStop}
          shipment={selectedAction.shipment}
          show={!!selectedAction}
          onSave={handleScheduleAppt}
          onHide={handleCloseNextActionModal}
        />
      );
    }

    if (selectedAction.action === ENTER_REQUIRED_DATES) {
      return null;
    }

    if (selectedAction.action === ADD_CONTAINER) {
      return (
        <StopEquipmentModal
          show={!!selectedAction}
          shipmentId={selectedAction.shipment._id}
          stopId={selectedAction.nextStop._id}
          onStopEquipmentUpdateSuccess={handleStopEquipmentUpdateSuccess}
          onStopEquipmentUpdateFail={handleStopEquipmentUpdateFail}
          onHide={handleCloseNextActionModal}
          isCarrier={isCustomerView}
        />
      );
    }

    if (selectedAction.action === PROVIDE_ARRIVAL_INFORMATION) {
      return (
        <StopArrivedModal
          shipmentId={selectedAction.shipment._id}
          stopId={selectedAction.nextStop._id}
          show={!!selectedAction}
          onHide={handleCloseNextActionModal}
          onStopArrived={ShipmentsActionCreators.transitionStop}
        />
      );
    }
    const isDrayageDocumentRequirements =
      selectedAction.action === ADD_DOCUMENTS &&
      selectedAction?.shipment?.shipment_type !== 'shorthaul';
    if (isDrayageDocumentRequirements) {
      return (
        <CargomaticModal
          size="large"
          style={{ paddingTop: 200 }}
          title={t('PROVIDE_SHIPMENT_DOCUMENTS')}
          onHide={handleCloseNextActionModal}
        >
          <div>
            {renderWarningForAccessorialDocumentsConitionally(
              selectedAction.shipment,
            )}
            <div style={{ padding: 20 }}>
              <MinimumDocumentRequirements
                shipment={selectedAction.shipment}
                title="PLEASE_PROVIDE_THE_FOLLOWING_DOCUMENTS"
              />
            </div>
          </div>
        </CargomaticModal>
      );
    }

    return (
      <StopCompleteModal
        show={!!selectedAction}
        shipmentId={selectedAction.shipment._id}
        stopId={selectedAction.nextStop._id} // things will break because API does not contain details of next stop
        onStopComplete={handleStopComplete}
        onHide={handleCloseNextActionModal}
        onStopEquipmentUpdateSuccess={handleStopEquipmentUpdateSuccess}
        onStopEquipmentUpdateFail={handleStopEquipmentUpdateFail}
        isCarrier={isCustomerView}
        shipperStopDocumentRequirements={shipperStopDocumentRequirements}
      />
    );
  }

  return (
    <div className="divide-y divide-gray-200 divide-solid">
      {actions.map((action, i) => (
        <NextAction
          action={action}
          actionType={action}
          nextAction={action.nextStop?.action || action.action}
          isCustomerView={isCustomerView}
          onNextActionClick={handleNextActionClick}
          key={i}
          hideEdit={ACTIONS_WITHOUT_EDIT.includes(action.action)}
        />
      ))}
      {total > 0 && (
        <Pagination
          className=""
          perPage={perPage}
          page={page}
          pages={pages}
          total={total}
          onPageClick={handlePageClick}
        />
      )}
      {renderActionModal()}
    </div>
  );
}

NextActionsList.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      shipment_id: PropTypes.string,
      equipment: PropTypes.string,
      shipper: PropTypes.shape({
        _id: PropTypes.string,
        company_name: PropTypes.string,
      }),
      carrier: PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
      }),
      actionMessage: PropTypes.string,
    }),
  ),
  isCustomerView: PropTypes.bool,
  page: PropTypes.number,
  pages: PropTypes.number,
  perPage: PropTypes.number,
  total: PropTypes.number.isRequired,
};

NextActionsList.defaultProps = {
  isCustomerView: true,
  page: 1,
  pages: 1,
  perPage: 10,
};
